import { Icon, IconButton, Text, Tooltip, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { CoeOfferDetailEntity } from '@genial/services';
import { Utils, useBreakpointValue } from '@genial/utils';
import { Box } from 'native-base';
import React from 'react';

import { messageError, parseStructuredOperationContentHorizontalData } from '../../constants';
import { StructuredOperationContentHorizontalList } from '../StructuredOperationContentHorizontalList';
import { StructuredOperationSubDetailsSection } from '../StructuredOperationSubDetailsSection';

interface MarketStatusProps {
  isOpened: boolean;
  message: string;
}
interface StructuredOperationContentDetailsProps {
  marketStatus: MarketStatusProps;
  onClose: () => void;
  data: CoeOfferDetailEntity;
  strategy: string;
}
export const StructuredOperationContentDetails: React.FC<
  StructuredOperationContentDetailsProps
> = ({ marketStatus, onClose, data, strategy }) => {
  const isMobile = useBreakpointValue({ base: true, sm: false });

  const [sectionState, setSectionState] = React.useState({
    operation: false,
    composition: false,
    'investment-simulator': false,
    'cost-simulator': false,
  });

  const onOpenSection = (type: keyof typeof sectionState) => {
    setSectionState((oldState) => {
      const newState = { ...oldState };
      const isOpen = newState[type];

      Object.keys(newState).forEach((key) => {
        newState[key as keyof typeof newState] = false;
      });

      newState[type] = !isOpen;

      return newState;
    });
  };

  const operationName =
    data?.companyFantasyName && data?.asset
      ? `${data?.companyFantasyName} ${data?.asset}`
      : messageError;

  const scenarios = [data?.highScenario, data?.lowScenario, data?.optionalScenario];

  const structuredOperationContentHorizontalData =
    parseStructuredOperationContentHorizontalData(data);

  return (
    <VStack
      w={{ base: 'full', lg: '910px' }}
      bg="neutral.white"
      borderRadius={BoxRadius.sm}
      space={{ base: Spacing['inline.xxxs'], sm: 0 }}>
      <Box
        w={{ base: Spacing['inline.md'], lg: 0 }}
        background={'neutral.lightest'}
        borderRadius={BoxRadius.sm}
        h={{ base: 1, lg: 0 }}
        alignSelf={'center'}
        mt={Spacing['inline.xxxs']}
      />
      <VStack
        space={Spacing['inline.xxxs']}
        px={{ base: Spacing['inset.xs'], sm: Spacing['inset.md'] }}
        pb={{ base: Spacing['inset.xs'], sm: Spacing['inset.md'] }}>
        <HStack justifyContent={'space-between'} alignItems={'flex-start'}>
          <VStack space={Spacing['inline.quarck']}>
            {!isMobile && (
              <Text variant="regular" color={'neutral.dark'} fontSize={Typography['fontsize.md']}>
                Detalhes da operação
              </Text>
            )}
            <Text
              variant={isMobile ? 'bold' : 'regular'}
              color={'neutral.black'}
              fontSize={{ base: Typography['fontsize.lg'], sm: Typography['fontsize.xxl'] }}>
              {operationName}
            </Text>
          </VStack>
          {!isMobile && (
            <IconButton
              testId={'icon-close'}
              icon="Close"
              iconSize={'xxxxs'}
              borderWidth={{ base: 0, lg: '1px' }}
              variant="rounded"
              backgroundColor="transparent"
              iconColor={Color['brand.primary.principal']}
              borderColor={Color['brand.primary.principal']}
              onPress={onClose}
            />
          )}
        </HStack>
        {!isMobile && <Box w={'full'} bg={'neutral.lightest'} h={'0.5'} />}
        <HStack w={'full'} alignItems={'center'} justifyContent={'space-between'}>
          <HStack alignItems={'center'} space={Spacing['inline.quarck']}>
            <Text color={'neutral.dark'} variant="semibold" fontSize={Typography['fontsize.md']}>
              Estratégia:
            </Text>
            <Text color={'neutral.dark'} variant="regular" fontSize={Typography['fontsize.md']}>
              {strategy || messageError}
            </Text>
          </HStack>
          {marketStatus && !marketStatus.isOpened && (
            <Tooltip
              testID="qa"
              hasArrow
              label={Utils.Lines.breakIntoLines(marketStatus.message || '', 7)}>
              <HStack
                bg={Utils.ColorToken.WithOpacity('feedback.attention.lightest', 'opacity.medium')}
                space={Spacing['inline.quarck']}
                px={Spacing['inline.xxxs']}
                py={Typography['fontsize.xs']}
                borderRadius={BoxRadius.sm}
                alignItems={'center'}>
                <Icon
                  testID="market-close-caption"
                  name="Warning"
                  size="xxs"
                  color={Color['feedback.attention.default']}
                />
                <Text
                  variant={'semibold'}
                  fontSize={Typography['fontsize.xs']}
                  lineHeight="distant">
                  Mercado fechado
                </Text>
              </HStack>
            </Tooltip>
          )}
        </HStack>
        <Text
          color={'brand.primary.principal'}
          variant="regular"
          fontSize={Typography['fontsize.md']}
          pr={Spacing['inline.md']}>
          {data?.offerDescription || messageError}
        </Text>
        <StructuredOperationContentHorizontalList data={structuredOperationContentHorizontalData} />
        <Box w={'full'} bg="neutral.white" h={'0.5'} />
        <StructuredOperationSubDetailsSection
          type="operation"
          scenarios={scenarios}
          table={data?.table}
          title="Sobre a operação"
          isOpened={sectionState.operation}
          onOpen={() => onOpenSection('operation')}
        />
        <StructuredOperationSubDetailsSection
          type="composition"
          operation={operationName}
          composition={data?.composition}
          title="Composição"
          isOpened={sectionState.composition}
          onOpen={() => onOpenSection('composition')}
        />
        <StructuredOperationSubDetailsSection
          type="investment-simulator"
          title="Simulador de investimentos"
          onOpen={() => onOpenSection('investment-simulator')}
          isOpened={sectionState['investment-simulator']}
          investmentSimulatorChartData={data?.investmentSimulator?.lines || []}
          marketIsOpened={marketStatus.isOpened}
          minValue={data?.minimumApplication}
          onClose={onClose}
        />
        <StructuredOperationSubDetailsSection
          type="cost-simulator"
          title="Simulador de custos"
          onOpen={() => onOpenSection('cost-simulator')}
          isOpened={sectionState['cost-simulator']}
          marketIsOpened={marketStatus.isOpened}
          minValue={data?.minimumApplication}
          costSimulator={data?.costSimulator}
          onClose={onClose}
        />
      </VStack>
    </VStack>
  );
};
